/**
 * <StudentPrerequisiteQuizPage> — le quiz de prérequis d'une activité, à la place de son énoncé
 * (page).
 *
 * L'ancien quiz (`PrerequisiteQuizGate`, puis `SamCheckpointOverlay` en mode quiz) couvrait toute
 * l'activité : l'énoncé ne se lit pas avant le quiz, au téléphone comme sur un grand écran, en live
 * comme hors live (Wajih, 18/10 : « chaque question est à part, comme dans une vue à part »). Une
 * étape à la fois (`step`, contrôlée) :
 *  - `checking` : le quiz se lit, avant l'intro (introuvable ou vide, l'activité s'ouvre sans elle) :
 *    la forme de la page, sans rien dire encore ;
 *  - `intro` : Sam, « Avant de commencer l'activité, vérifions tes connaissances », et « Commencer le
 *    quiz » (ou, le quiz pas chargé, « Réessayer ») ;
 *  - `question` : « Question 2 sur 5 » et l'avancée, la question (une formule se rend comme
 *    l'énoncé, `textFormat="rich"`), ses réponses : une seule se choisit, puis « Question suivante »
 *    (« Voir mon résultat » à la dernière). La précédente disparaît : il n'y a pas de fil. Le quiz ne
 *    dit pas juste ou faux à chaque question, l'ancien non plus : le serveur ne sert la bonne réponse
 *    qu'après la tentative, et la correction la montre ensuite. « Un souci avec cette question ? »
 *    ouvre le signalement (`onSignal`) ;
 *  - `result` : le résultat de Sam (« Bravo ! Tu as réussi le quiz avec 4/5 (80%)… »), le pétale
 *    gagné, « C'était clair ? », et « Voir ma correction ».
 * La croix (`onClose`, quand l'écran l'offre) referme le quiz ; il reprend ensuite à la question où il
 * s'était arrêté. La correction est `<QuizReview>`, posée par l'écran sur la même place.
 *
 * Le même geste que l'attente de la correction (Wajih, 20/10) : un seul gabarit, Sam sur scène à
 * l'intro qui **se range** dans l'en-tête à la première question (`<ChampionStage>`), la question
 * à côté de lui (« Question 2 sur 5 », l'avancée) et dessous ; les questions s'enchaînent par
 * `<StepStage>`. Au résultat, Sam reste rangé et dit son mot à côté. Un seul visage, sa tête V16.
 * En live, la bulle tandem reste au coin : posée une fois au-dessus des étapes (`<FloatingSlot>`).
 *
 * La mise en page est celle de l'énoncé (`<StudentActivityPage>`, Wajih au daily du 21/10 : « reproduire
 * l'affichage qui a été choisi pour l'énoncé de l'activité ») : la question prend toute la hauteur
 * au-dessus du pied, le bouton est dans le pied fixe, en bas, et la bulle se pose au-dessus de ce pied ;
 * la page lui garde sa place en fin de défilement (`useFloatingReserve`), comme sous l'énoncé. Avant, la
 * place de la bulle était retirée de toute la hauteur : au téléphone, la question s'arrêtait une bulle
 * plus haut que le pied, et une longue question se coupait au-dessus d'une bande vide.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Skeleton } from "../../atoms/Skeleton"
import { StepStage } from "../../atoms/StepStage"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { CHAMPION_STAGE_SIZE, ChampionStage } from "../../molecules/ChampionStage"
import { CheckpointReward } from "../../molecules/CheckpointReward"
import { ConversationClose, ConversationHeader } from "../../molecules/ConversationHeader"
import { CorrectionFeedback, type UICorrectionFeedback } from "../../molecules/CorrectionFeedback"
import { CorrectionMoment } from "../../molecules/CorrectionMoment"
import { QuizChoiceRow } from "../../molecules/QuizChoiceRow"
import { quizChoiceLetter } from "../../molecules/QuizChoiceRow/QuizChoiceRow"
import { RichText } from "../../molecules/RichText"
import { PageTemplate } from "../../templates/PageTemplate"
import { useFloatingReserve } from "../../templates/PageTemplate/FloatingSlot"

export type UIPrerequisiteQuizOption = {
    key: string
    label: string
    /** `rich` : la réponse porte une formule. Défaut `plain`. */
    labelFormat?: "plain" | "rich"
}

export type StudentPrerequisiteQuizStep =
    /** Le quiz se lit, avant l'intro : l'écran ne la pose que pendant cette lecture. */
    | { kind: "checking" }
    | {
          kind: "intro"
          /** Le quiz se charge : le bouton tourne. */
          loading: boolean
          /** Le quiz n'a pas pu se charger : la phrase de l'échec, et « Réessayer ». */
          failed: boolean
      }
    | {
          kind: "question"
          /** À partir de 0. */
          index: number
          total: number
          text: string
          /** `rich` : la question porte une formule. Défaut `plain`. */
          textFormat?: "plain" | "rich"
          options: UIPrerequisiteQuizOption[]
          /** La réponse choisie, pas encore envoyée. */
          selectedKey: string | null
          /** La réponse part (la dernière : la tentative). */
          sending: boolean
          /** La tentative n'est pas partie : la phrase de l'échec ; la croix referme le quiz. */
          failed: boolean
      }
    | {
          kind: "result"
          /** Ce que Sam dit du résultat, retours à la ligne compris. */
          text: string
          /** Les réponses données : la barre du pétale. */
          answered: number
          earned: boolean
          feedback: UICorrectionFeedback | null
          feedbackSubmitting: boolean
      }

export type StudentPrerequisiteQuizPageProps = {
    /** Le titre de l'activité. */
    title: string
    /**
     * Où se lit le titre. `bar` (défaut) : l'en-tête de l'écran. `inline` : dans le volet du live,
     * sous le seul en-tête du live (le titre ouvre le contenu, la croix à côté).
     */
    heading?: "bar" | "inline"
    /** Le retour, à l'étape `intro` (et pendant la lecture d'avant). Absent : pas de flèche (en live). */
    onBack?: () => void
    step: StudentPrerequisiteQuizStep
    /** « Commencer le quiz », « Réessayer ». */
    onStart: () => void
    onSelect: (key: string) => void
    /** « Question suivante », « Voir mon résultat ». */
    onNext: () => void
    /** La croix : referme le quiz. Absente : pas de croix (pas encore permise). */
    onClose?: () => void
    onFeedback: (value: UICorrectionFeedback) => void
    /** « Voir ma correction ». */
    onSeeCorrection: () => void
    /** « Un souci avec cette question ? ». Absent : pas de lien. */
    onSignal?: () => void
    testID?: string
}

/** Une question se lit dans une colonne de téléphone, même sur un grand écran. */
const QUIZ_WIDTH = 560

export const PREREQUISITE_QUIZ_TEXTS = {
    intro: "Avant de commencer l'activité, vérifions tes connaissances",
    start: "Commencer le quiz",
    loadError: "Impossible de charger le quiz. Réessaie.",
    retry: "Réessayer",
    progress: (index: number, total: number) => `Question ${index + 1} sur ${total}`,
    next: "Question suivante",
    last: "Voir mon résultat",
    submitError:
        "Oups, ton quiz n'a pas pu être envoyé. Ferme cette fenêtre et reprends-le : tes réponses sont gardées.",
    signal: "Un souci avec cette question ?",
    seeCorrection: "Voir ma correction",
    close: "Fermer le quiz",
    feedback: { question: "C'était clair ?", yes: "C'était clair", no: "Ce n'était pas clair" },
}

export function StudentPrerequisiteQuizPage(props: StudentPrerequisiteQuizPageProps) {
    const { title, heading = "bar", onBack, step, onClose, testID } = props
    const checking = step.kind === "checking"
    const close =
        onClose != null ? (
            <ConversationClose onPress={onClose} accessibilityLabel={PREREQUISITE_QUIZ_TEXTS.close} />
        ) : null

    return (
        <PageTemplate
            header={
                heading === "bar" ? (
                    <ConversationHeader
                        name={title}
                        onBack={step.kind === "intro" || checking ? onBack : undefined}
                        maxContentWidth={QUIZ_WIDTH}
                        end={close ?? undefined}
                    />
                ) : undefined
            }
            scroll={false}
            maxContentWidth={QUIZ_WIDTH}
            padding="4"
            reserveFloating={false}
            loading={checking}
            skeleton={<CheckingSkeleton />}
            footer={checking ? undefined : <Footer {...props} />}
            testID={testID}
        >
            <View style={styles.fill}>
                {heading === "inline" && (
                    <Row gap="3" align="center" style={styles.inlineHeading}>
                        <Text
                            size="text-lg"
                            weight="bold"
                            color="text-strong"
                            style={styles.grow}
                            accessibilityRole="header"
                        >
                            {title}
                        </Text>
                        {close}
                    </Row>
                )}
                <Body {...props} />
            </View>
        </PageTemplate>
    )
}

/** Le quiz se lit : la place de Sam et de sa phrase, rien de plus (il peut ne pas y avoir de quiz). */
function CheckingSkeleton() {
    return (
        <Stack gap="4" align="center" style={styles.checking}>
            <Skeleton shape="circle" size={CHAMPION_STAGE_SIZE} />
            <Skeleton textSize="text-2xl" width={280} />
        </Stack>
    )
}

/**
 * Sam sur scène à l'intro, puis rangé dans l'en-tête dès la première question, jusqu'au résultat.
 * Les questions suivent toujours l'intro, que montrait une autre instance (la conversation du quiz se
 * monte neuve au « Commencer ») : rangé au montage, Sam part donc de sa place d'intro (`arriving`).
 */
function Body(props: StudentPrerequisiteQuizPageProps) {
    const { step } = props
    // La place de la bulle tandem du live, en fin de défilement : la fin de la question remonte au-dessus.
    const floatingRoom = useFloatingReserve()
    if (step.kind === "checking") return null
    return (
        <ChampionStage
            name="sam"
            docked={step.kind !== "intro"}
            arriving
            stage={
                <CorrectionMoment title={PREREQUISITE_QUIZ_TEXTS.intro} champion={false}>
                    {step.kind === "intro" && step.failed && (
                        <Banner
                            color="danger"
                            tinted
                            icon="exclamation-circle"
                            message={PREREQUISITE_QUIZ_TEXTS.loadError}
                        />
                    )}
                </CorrectionMoment>
            }
            dock={<Dock step={step} />}
        >
            {/* Une question à la fois, comme les étapes : la précédente sort, la suivante entre. */}
            <StepStage stepKey={step.kind === "question" ? `question-${step.index}` : step.kind} fill>
                <ScrollView
                    style={styles.fill}
                    contentContainerStyle={styles.scroll(floatingRoom)}
                    showsVerticalScrollIndicator={false}
                >
                    <Step {...props} />
                </ScrollView>
            </StepStage>
        </ChampionStage>
    )
}

/** À côté de Sam rangé : où en est le quiz, ou ce qu'il dit du résultat. */
function Dock({ step }: { step: StudentPrerequisiteQuizStep }) {
    if (step.kind === "question") {
        return (
            <Stack gap="2">
                <Text size="text-sm" weight="semibold" color="text-muted">
                    {PREREQUISITE_QUIZ_TEXTS.progress(step.index, step.total)}
                </Text>
                <ProgressBar value={step.total > 0 ? (step.index / step.total) * 100 : 0} height={6} />
            </Stack>
        )
    }
    if (step.kind === "result") {
        return (
            <Text size="text-lg" weight="semibold" color="text-strong">
                {step.text}
            </Text>
        )
    }
    return null
}

/** Sous l'en-tête : la question et ses réponses, ou le pétale et l'avis. */
function Step(props: StudentPrerequisiteQuizPageProps) {
    const { step, onSelect, onFeedback, onSignal } = props

    if (step.kind === "result") {
        return (
            <Stack gap="5">
                <Row justify="center">
                    <CheckpointReward answered={step.answered} earned={step.earned} />
                </Row>
                <CorrectionFeedback
                    value={step.feedback}
                    onChange={onFeedback}
                    submitting={step.feedbackSubmitting}
                    texts={PREREQUISITE_QUIZ_TEXTS.feedback}
                />
            </Stack>
        )
    }
    if (step.kind !== "question") return null

    const locked = step.sending
    return (
        <Stack gap="4">
            <Card p="4">
                {step.textFormat === "rich" ? (
                    <RichText value={step.text} />
                ) : (
                    <Text size="text-lg" weight="semibold" color="text-strong">
                        {step.text}
                    </Text>
                )}
            </Card>
            <Stack gap="2">
                {step.options.map((option, index) => (
                    <QuizChoiceRow
                        key={option.key}
                        letter={quizChoiceLetter(index)}
                        label={option.label}
                        labelFormat={option.labelFormat}
                        state={option.key === step.selectedKey ? "selected" : locked ? "inert" : "idle"}
                        onPress={locked ? undefined : () => onSelect(option.key)}
                    />
                ))}
            </Stack>
            {step.failed && (
                <Banner color="danger" tinted icon="exclamation-circle" message={PREREQUISITE_QUIZ_TEXTS.submitError} />
            )}
            {onSignal != null && (
                <View style={styles.center}>
                    <TextButton label={PREREQUISITE_QUIZ_TEXTS.signal} color="text-muted" onPress={onSignal} />
                </View>
            )}
        </Stack>
    )
}

function Footer(props: StudentPrerequisiteQuizPageProps) {
    const { step, onStart, onNext, onSeeCorrection } = props
    if (step.kind === "checking") return null
    if (step.kind === "intro") {
        return (
            <Button
                label={step.failed ? PREREQUISITE_QUIZ_TEXTS.retry : PREREQUISITE_QUIZ_TEXTS.start}
                iconName="play"
                onPress={onStart}
                loading={step.loading}
                fullWidth
            />
        )
    }
    if (step.kind === "result") {
        return <Button label={PREREQUISITE_QUIZ_TEXTS.seeCorrection} onPress={onSeeCorrection} fullWidth />
    }
    const last = step.index >= step.total - 1
    return (
        <Button
            label={last ? PREREQUISITE_QUIZ_TEXTS.last : PREREQUISITE_QUIZ_TEXTS.next}
            onPress={onNext}
            loading={step.sending}
            disabled={step.selectedKey == null || step.failed}
            fullWidth
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1, minHeight: 0 },
    grow: { flex: 1, minWidth: 0 },
    center: { alignItems: "center" },
    inlineHeading: { marginBottom: theme.spacing["5"] },
    scroll: (floatingRoom: number) => ({
        paddingBottom: floatingRoom > 0 ? floatingRoom + theme.spacing["8"] : theme.spacing["4"],
    }),
    /** La place de Sam sur scène : au milieu de la page, comme l'intro qui suit. */
    checking: { flex: 1, justifyContent: "center" },
}))
